import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { Operator } from '@img-arena/consoles-api-dde'
import { DataCard } from '@/components/card'
import { OperatorFormButton } from '@/components/forms/OperatorForm'
import {
  ActionCopyTokenItem,
  ActionDeleteOperatorItem,
  ActionsMenu
} from '@/components/table/actions'
import { FilterableTable } from '@/components/table/base'
import { CheckboxDeleteSelectedOperatorItem, CheckboxMenu } from '@/components/table/checkbox'
import { STORE_STATUS } from '@/constants/store'
import { ROUTE_PARAM, ROUTE_URL } from '@/router/constants'
import { createBulkHandler } from '@/service/bulkOperationsService'
import { ddeApi } from '@/service/ddeApi'
import { useOperatorsStoreDDE } from '@/stores/operators'

import { DDE_TABLE_COLUMNS, DDE_TABLE_FILTERS } from './operators.dde.table'

const tableId = 'dde-operators-table'

export const OperatorsViewDDE = () => {
  const navigate = useNavigate()

  const { data, isFetching, isLoading } = useOperatorsStoreDDE(
    useShallow((state) => ({
      data: state.data,
      isLoading: state.status === STORE_STATUS.loading,
      isFetching: state.status === STORE_STATUS.fetching
    }))
  )

  const { fetchData, resetState } = useOperatorsStoreDDE(
    useShallow((state) => ({
      fetchData: state.fetchData,
      resetState: state.resetState
    }))
  )

  useEffect(() => {
    fetchData()
    return resetState
  }, [])

  const handleDeleteOperator = async (operator: Operator) => {
    await ddeApi.endpoints.operators.delete(operator.id)
    await fetchData()
  }

  const handleDeleteSelectedOperators = createBulkHandler({
    name: 'Delete Operators',
    operation: async (operator: Operator) => {
      await ddeApi.endpoints.operators.delete(operator.id)
    },
    refreshData: fetchData,
    getItemDisplayName: (operator) => `${operator.name} (ID: ${operator.id})`
  })

  const getOperatorToken = (operator: Operator) =>
    ddeApi.endpoints.operators.getToken(operator.id).then((response) => response.data.token)

  return (
    <DataCard
      title="Operators"
      headerPostfixActions={
        <>
          <CheckboxMenu tableId={tableId}>
            <CheckboxDeleteSelectedOperatorItem onDelete={handleDeleteSelectedOperators} />
          </CheckboxMenu>
          <OperatorFormButton.DDE onSubmit={fetchData} />
        </>
      }
    >
      <FilterableTable
        data={data}
        tableId={tableId}
        loading={isLoading}
        fetching={isFetching}
        filters={DDE_TABLE_FILTERS}
        columns={DDE_TABLE_COLUMNS}
        clientSideSortAndPagination
        clientSideFilters
        withCheckboxes
        onRowClick={(row) =>
          navigate(
            ROUTE_URL.OPERATORS.OPERATOR_LICENSES.replace(
              `:${ROUTE_PARAM.OPERATOR_ID}`,
              String(row.id)
            )
          )
        }
        rowActions={
          <ActionsMenu>
            <ActionCopyTokenItem getValueToCopy={getOperatorToken} />
            <ActionDeleteOperatorItem onDelete={handleDeleteOperator} />
          </ActionsMenu>
        }
      />
    </DataCard>
  )
}
